const DOMAIN = new TextEncoder().encode("rhema-safety-number-v1");
const GROUPS = 12;
const GROUP_BYTES = 5;

function decodeKey(key: string): Uint8Array {
  let binary: string;
  try {
    binary = atob(key);
  } catch {
    throw new Error("safety number key is not valid base64");
  }
  if (binary.length !== 32) throw new Error("safety number key must be a 32 byte Ed25519 key");
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

function compareBytes(a: Uint8Array, b: Uint8Array): number {
  for (let i = 0; i < a.length; i++) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/**
 * A 60 digit number, shown as 12 groups of 5, that two people compare to verify each
 * other's devices.
 *
 * Both sides compute the same value: the two Ed25519 identity keys (base64, as
 * vodozemac produces them) are decoded and sorted bytewise, then hashed as
 * SHA-512("rhema-safety-number-v1" || 0x00 || lower key || higher key) using WebCrypto.
 * Each group is the next 5 bytes of the digest read as a big-endian integer, mod 100000,
 * padded to 5 digits. 12 groups use 60 of the 64 digest bytes.
 *
 * The number changes whenever either key changes. It is a display aid only and adds no
 * cryptography beyond SHA-512.
 */
export async function safetyNumber(myEd25519: string, theirEd25519: string): Promise<string> {
  const keys = [decodeKey(myEd25519), decodeKey(theirEd25519)].sort(compareBytes);
  const input = new Uint8Array(DOMAIN.length + 1 + 64);
  input.set(DOMAIN, 0);
  input[DOMAIN.length] = 0;
  input.set(keys[0] as Uint8Array, DOMAIN.length + 1);
  input.set(keys[1] as Uint8Array, DOMAIN.length + 33);

  const digest = new Uint8Array(await globalThis.crypto.subtle.digest("SHA-512", input));
  const groups: string[] = [];
  for (let g = 0; g < GROUPS; g++) {
    let value = 0;
    for (let i = 0; i < GROUP_BYTES; i++) {
      value = value * 256 + (digest[g * GROUP_BYTES + i] ?? 0);
    }
    groups.push(String(value % 100000).padStart(5, "0"));
  }
  return groups.join(" ");
}
